type AsyncFn<TArgs extends unknown[] = unknown[], TResult = unknown> = (..._args: TArgs) => Promise<TResult>

interface ReusePromiseOptions {
  memoize?: boolean
  serializeArguments?: (_args: unknown[]) => string
}

type ReuseWrappedFn<TArgs extends unknown[], TResult> = ((..._args: TArgs) => Promise<TResult>) & {
  __reusePromise__origFn: AsyncFn<TArgs, TResult>
  __reusePromise__clear: () => void
  __reusePromise__promiseMapsByArgs: Map<string, Promise<TResult>>
}

const allPromiseMapsByArgs = new Set<Map<string, Promise<unknown>>>()

function defaultSerializeArguments (args: unknown[]): string {
  return JSON.stringify(args)
}

function clearMap (promiseMapByArgs: Map<string, Promise<unknown>>): void {
  promiseMapByArgs.clear()
}

function clear (fn?: { __reusePromise__promiseMapsByArgs?: Map<string, Promise<unknown>> }): void {
  if (fn?.__reusePromise__promiseMapsByArgs) {
    clearMap(fn.__reusePromise__promiseMapsByArgs)
    return
  }

  allPromiseMapsByArgs.forEach(clearMap)
}

function reusePromise<TArgs extends unknown[], TResult> (
  origFn: AsyncFn<TArgs, TResult>,
  options: ReusePromiseOptions = {}
): ReuseWrappedFn<TArgs, TResult> {
  const mergedOptions = {
    memoize: false,
    serializeArguments: defaultSerializeArguments,
    ...options
  }

  const promiseMapByArgs = new Map<string, Promise<TResult>>()

  const wrappedFn = function (this: unknown, ...args: TArgs): Promise<TResult> {
    const key = mergedOptions.serializeArguments(args)

    const existingPromise = promiseMapByArgs.get(key)
    if (existingPromise) return existingPromise

    const forgetPromise = (): void => {
      promiseMapByArgs.delete(key)
    }

    const origPromise = origFn.apply(this, args)
    const trackedPromise = origPromise.then(
      value => {
        if (!mergedOptions.memoize) forgetPromise()
        return value
      },
      error => {
        forgetPromise()
        throw error
      }
    )

    promiseMapByArgs.set(key, trackedPromise)
    return trackedPromise
  } as ReuseWrappedFn<TArgs, TResult>

  wrappedFn.__reusePromise__origFn = origFn
  wrappedFn.__reusePromise__promiseMapsByArgs = promiseMapByArgs
  wrappedFn.__reusePromise__clear = () => {
    clear(wrappedFn)
  }

  allPromiseMapsByArgs.add(promiseMapByArgs as Map<string, Promise<unknown>>)
  return wrappedFn
}

function decorator (options: ReusePromiseOptions = {}) {
  return function (
    _target: unknown,
    _name: string,
    descriptor: TypedPropertyDescriptor<AsyncFn>
  ): TypedPropertyDescriptor<AsyncFn> {
    if (!descriptor.value) return descriptor
    descriptor.value = reusePromise(descriptor.value, options)
    return descriptor
  }
}

;(decorator as typeof decorator & { clear: typeof clear }).clear = clear
;(reusePromise as typeof reusePromise & { clear: typeof clear }).clear = clear

export { clear, decorator }
export default reusePromise
